import { NiceModalHandler } from '@ebay/nice-modal-react';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'motion/react';
import React, { PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useEventListener, useWindowSize } from 'usehooks-ts';
import { borderRadius, transition } from '../../foundations';
import { elevationShadow } from '../../foundations/elevation/elevation';
import { color } from '../../foundations/modes/colors/colors';
import { spacing } from '../../foundations/spacing/spacing';
import {
  ModalPopoverAnchor,
  ModalPopoverDirection,
  ModalPopoverMaxSize,
  ModalPopOverParentSelector,
  ModalPopoverPosition,
  ModalPopoverSize,
} from './types/modalPopover.types';
import {
  calculateMaxSize,
  calculateModalOverflow,
  calculatePopoverDirection,
  calculatePosition,
  calculateShiftedPosition,
  calculateSize,
  calculateTransformOrigin,
  getParentAnchors,
  getSpaceBetweenButtonAndContainer,
  isEventInsideAnyModal,
} from './utils/modalPopover.utils';

export interface ModalPopoverProps {
  modal: NiceModalHandler;
  contentLabel: string;
  parentSelector: ModalPopOverParentSelector;
  /**
   * Prefered direction of the modal. If the modal is too large to fit in the container, the direction will be adjusted to fit.
   */
  direction?: ModalPopoverDirection;
  modalAnchor?: ModalPopoverAnchor;
  offset?: number;
  spaceBetweenParent?: spacing;
  spaceBetweenContainer?: spacing;
  containerData?: { position: ModalPopoverPosition; size: ModalPopoverSize } | undefined;
  /** return false to prevent closing */
  onBeforeClose?: () => boolean | void;
  shouldFocusAfterRender?: boolean;
  /** Prevent scrolling outside of the modal. Defaults to true. */
  preventScrollOutside?: boolean;
  className?: string;
}

export function ModalPopover({
  modal,
  contentLabel,
  parentSelector,
  direction: targetDirection = 'right',
  modalAnchor = 'center',
  offset = 0,
  spaceBetweenParent = spacing.size8,
  spaceBetweenContainer = spacing.size8,
  containerData = undefined,
  onBeforeClose,
  shouldFocusAfterRender = true,
  preventScrollOutside = true,
  className,
  children,
}: PropsWithChildren<ModalPopoverProps>) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<ModalPopoverPosition>({ top: 0, left: 0 });
  const windowSize = useWindowSize();
  const verticalScrollPosition = window.scrollY;
  const container = containerData
    ? containerData
    : { position: { top: verticalScrollPosition, left: 0 }, size: windowSize };
  const [maxSize, setMaxSize] = useState<ModalPopoverMaxSize>({
    maxWidth: undefined,
    maxHeight: undefined,
  });
  const [initialModalSize, setInitialModalSize] = useState<ModalPopoverSize | undefined>(undefined);
  const [shouldRender, setShouldRender] = useState(modal.visible);
  const [direction, setDirection] = useState<ModalPopoverDirection>(targetDirection);

  const transformModal = () => {
    const parent = parentSelector();
    const parentBoundingRect = parent?.getBoundingClientRect();
    if (!initialModalSize || !contentRef.current || parentBoundingRect === undefined) return;

    const buttonAnchors = getParentAnchors(modalAnchor, parentBoundingRect, container.position);
    const spaceBetweenButtonAndContainer = getSpaceBetweenButtonAndContainer(
      buttonAnchors,
      container,
      spaceBetweenContainer,
      spaceBetweenParent
    );
    const calculatedDirection = calculatePopoverDirection(
      targetDirection,
      spaceBetweenButtonAndContainer,
      initialModalSize
    );
    setDirection(calculatedDirection);

    const parentAnchorPosition = buttonAnchors[calculatedDirection];
    const initialPosition = calculatePosition(
      parentAnchorPosition,
      modalAnchor,
      calculatedDirection,
      initialModalSize,
      offset,
      spaceBetweenParent
    );
    const overflow = calculateModalOverflow(initialModalSize, initialPosition, container, spaceBetweenContainer);
    const correctedSize = calculateSize(
      calculatedDirection,
      initialModalSize,
      container.size,
      spaceBetweenContainer,
      overflow
    );
    const correctedPosition = calculateShiftedPosition(
      calculatePosition(
        parentAnchorPosition,
        modalAnchor,
        calculatedDirection,
        correctedSize,
        offset,
        spaceBetweenParent
      ),
      calculatedDirection,
      container.position,
      spaceBetweenContainer,
      overflow
    );
    setPosition(correctedPosition);
    const newMaxSize = calculateMaxSize(calculatedDirection, correctedSize, container.size, spaceBetweenContainer);
    setMaxSize(newMaxSize);
  };

  const handleClose = () => {
    if (onBeforeClose?.() === false) return;
    modal.hide();
  };

  useEventListener('mousedown', (event) => {
    if (!isEventInsideAnyModal(event)) {
      handleClose();
    }
  });

  const preventScrollOutsideModal = (event: Event) => {
    if (preventScrollOutside && !isEventInsideAnyModal(event)) {
      event.preventDefault();
    }
  };
  const documentRef = useRef<Document>(document);

  useEventListener('wheel', preventScrollOutsideModal, documentRef, { passive: false });
  useEventListener('touchmove', preventScrollOutsideModal, documentRef, { passive: false });

  useEffect(() => {
    const contentBoundingRect = contentRef.current?.getBoundingClientRect();
    if (contentBoundingRect)
      setInitialModalSize({ width: contentBoundingRect.width, height: contentBoundingRect.height });
  }, [children]);

  useLayoutEffect(() => {
    transformModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize, initialModalSize]);

  useEffect(() => {
    if (modal.visible) setShouldRender(true);
  }, [modal.visible]);

  const handleExitAnimationComplete = () => {
    if (!modal.visible) setShouldRender(false);
  };

  return (
    <ReactModal
      isOpen={shouldRender}
      shouldCloseOnEsc={true}
      shouldFocusAfterRender={shouldFocusAfterRender}
      contentLabel={contentLabel}
      onAfterClose={() => modal.remove()}
      onRequestClose={handleClose}
      contentRef={(node: HTMLDivElement) => {
        contentRef.current = node;
        const contentBoundingRect = contentRef.current?.getBoundingClientRect();
        if (!contentBoundingRect) return;
        setInitialModalSize({ width: contentBoundingRect.width, height: contentBoundingRect.height });
      }}
      style={{
        overlay: {
          position: 'absolute',
          zIndex: 1000,
          top: position.top,
          left: position.left,
          backgroundColor: 'transparent',
          width: 'max-content',
          height: 'fit-content',
        },
        content: {
          width: 'max-content',
          height: 'fit-content',
          maxWidth: maxSize.maxWidth,
          maxHeight: maxSize.maxHeight,
          inset: 0,
          padding: 0,
          border: 'none',
          borderRadius: 'none',
          overflow: 'inherit',
          backgroundColor: 'transparent',
        },
      }}
    >
      {/* We handle the styling of the modal here so we can animate the content. We could not get the ReactModel itself to animate with motion, which we wanted to use to create a smooth entry and exit transition. */}
      <AnimatePresence onExitComplete={handleExitAnimationComplete}>
        {modal.visible && (
          <AnimationWrapper
            key="modal-content"
            className={className}
            maxWidth={maxSize.maxWidth}
            maxHeight={maxSize.maxHeight}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: transition.duration.fast.number, ease: 'easeOut' }}
            style={{
              transformOrigin: calculateTransformOrigin(direction),
            }}
          >
            <ChildrenWrapper maxHeight={maxSize.maxHeight}>{children}</ChildrenWrapper>
          </AnimationWrapper>
        )}
      </AnimatePresence>
    </ReactModal>
  );
}

const ChildrenWrapper = styled.div<{
  maxWidth?: number;
  maxHeight?: number;
}>`
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth - 2}px` : 'none')};
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight - 2}px` : 'none')};
`;

const AnimationWrapper = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== 'maxWidth' && prop !== 'maxHeight',
})<{
  maxWidth?: number;
  maxHeight?: number;
}>`
  background-color: ${color.neutralNew.surface.fill};
  box-shadow: ${elevationShadow.e200};
  padding: 0;
  inset: 0;
  overflow: auto;
  width: max-content;
  height: fit-content;
  border: 1px solid ${color.neutralNew.surface.stroke};
  border-radius: ${borderRadius.rounded8};
  box-sizing: border-box;
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : 'none')};
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : 'none')};
`;
